import React from 'react';
import { AudioButton } from '../AudioButton';
import { MicrophoneButton } from '../MicrophoneButton';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, ArrowRight, Clock, Star, Loader2, Check, Play, Stethoscope } from 'lucide-react';

const moduleIcons = {
  "med_basics": Stethoscope, "med_vitals": Stethoscope, "med_pain": Stethoscope,
  "med_medication": Stethoscope, "med_body": Stethoscope, "med_nursing": Stethoscope,
  "med_communication": Stethoscope, "med_emergency": Stethoscope
};

export const MedicalLessonDetail = ({ lesson, onBack }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/10">
        <ChevronLeft className="h-5 w-5 mr-2" /> Back to Lessons
      </Button>
      <Badge className="bg-emerald-500">{lesson.level}</Badge>
    </div>
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-white text-2xl">{lesson.title_de}</CardTitle>
          <AudioButton text={lesson.title_de} voice="nova" size="sm" className="text-emerald-400 hover:text-emerald-300" />
        </div>
        <CardDescription className="text-slate-400 text-lg">{lesson.title_en}</CardDescription>
        <p className="text-slate-500 text-right" dir="rtl">{lesson.title_ar}</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {lesson.objectives?.length > 0 && (
          <div>
            <h3 className="text-emerald-400 font-semibold mb-2">Learning Objectives</h3>
            <ul className="list-disc list-inside text-slate-300 space-y-1">
              {lesson.objectives.map((obj, i) => <li key={`obj-${obj?.slice(0, 15)}-${i}`}>{obj}</li>)}
            </ul>
          </div>
        )}
        {lesson.vocabulary?.length > 0 && (
          <div>
            <h3 className="text-emerald-400 font-semibold mb-3">Vocabulary</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {lesson.vocabulary.map((word, i) => (
                <Card key={`vocab-${word.de?.slice(0, 10)}-${i}`} className="bg-slate-700/50 border-slate-600">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-white font-bold">{word.de}</p>
                      <div className="flex items-center gap-1">
                        <AudioButton text={word.de} voice="nova" size="sm" className="text-emerald-400" />
                        <MicrophoneButton expectedText={word.de} onPronunciationScore={(s) => { if (s.overall_score >= 80) toast.success(`Perfect! ${s.overall_score}%`); }} size="sm" className="text-emerald-400" />
                      </div>
                    </div>
                    <p className="text-emerald-400 text-sm">{word.en}</p>
                    <p className="text-slate-400 text-sm" dir="rtl">{word.ar}</p>
                    {word.example && (
                      <div className="flex items-center gap-1 mt-1">
                        <p className="text-slate-500 text-xs italic">&quot;{word.example}&quot;</p>
                        <AudioButton text={word.example} voice="nova" size="sm" className="text-slate-500 hover:text-emerald-300" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        {lesson.dialogues?.length > 0 && (
          <div>
            <h3 className="text-emerald-400 font-semibold mb-3">Practice Dialogue</h3>
            {lesson.dialogues.map((dialogue, i) => (
              <Card key={`dlg-${dialogue.situation?.slice(0, 10)}-${i}`} className="bg-slate-700/50 border-slate-600">
                <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Situation: {dialogue.situation}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {dialogue.lines.map((line, j) => (
                    <div key={`line-${line.speaker}-${j}`} className="border-l-2 border-emerald-500 pl-3">
                      <p className="text-emerald-400 text-xs font-semibold">{line.speaker}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-white flex-1">{line.de}</p>
                        <AudioButton text={line.de} voice={line.speaker === 'Pfleger' || line.speaker === 'Arzt' ? 'onyx' : 'nova'} size="sm" className="text-emerald-400" />
                        <MicrophoneButton expectedText={line.de} onPronunciationScore={(s) => { if (s.overall_score >= 70) toast.success(`Good! ${s.overall_score}%`); }} size="sm" className="text-emerald-400" />
                      </div>
                      <p className="text-slate-400 text-sm">{line.en}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
          <Check className="h-4 w-4 mr-2" /> Mark as Complete (+{lesson.points_reward} XP)
        </Button>
      </CardContent>
    </Card>
  </div>
);

export const MedicalModuleView = ({ module, onBack, onLessonClick, lessonLoading }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/10">
        <ChevronLeft className="h-5 w-5 mr-2" /> Back to Modules
      </Button>
      <Badge className="bg-emerald-500">{module.difficulty}</Badge>
    </div>
    <Card className="bg-slate-800/50 border-slate-700 mb-4">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-white text-xl">{module.title_de}</CardTitle>
          <AudioButton text={module.title_de} voice="nova" className="text-emerald-400" />
        </div>
        <CardDescription className="text-slate-400">{module.title_en}</CardDescription>
      </CardHeader>
    </Card>
    <h3 className="text-white font-semibold mb-2">Lessons ({module.lessons?.length || 0})</h3>
    <div className="space-y-3">
      {module.lessons?.map((lesson, index) => (
        <Card key={lesson.lesson_id} className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all cursor-pointer" onClick={() => onLessonClick(lesson)}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">{index + 1}</div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium">{lesson.title_de}</p>
                  <AudioButton text={lesson.title_de} voice="nova" size="sm" className="text-emerald-400" />
                </div>
                <p className="text-slate-400 text-sm">{lesson.title_en}</p>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{lesson.duration_minutes} min</span>
                  <span className="flex items-center gap-1"><Star className="h-3 w-3" />{lesson.points_reward} XP</span>
                </div>
              </div>
            </div>
            {lessonLoading ? <Loader2 className="h-5 w-5 animate-spin text-emerald-400" /> : <ArrowRight className="h-5 w-5 text-emerald-400" />}
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

export const MedicalModuleGrid = ({ modules, getTitle, onModuleClick }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
    {modules.map((module) => (
      <Card key={module.module_id} className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all cursor-pointer group" onClick={() => onModuleClick(module)} data-testid={`module-${module.module_id}`}>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="p-3 rounded-xl bg-emerald-500/20 text-emerald-400 group-hover:bg-emerald-500/30 transition-colors">
              <span className="text-2xl">{module.icon || '📚'}</span>
            </div>
            <Badge variant="outline" className="text-slate-400 border-slate-600">{module.difficulty}</Badge>
          </div>
          <CardTitle className="text-white text-lg mt-3">{getTitle(module)}</CardTitle>
          <CardDescription className="text-slate-400">{module.lessons_count} lessons</CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400">
            <Play className="h-4 w-4 mr-2" /> Start Learning
          </Button>
        </CardContent>
      </Card>
    ))}
  </div>
);
